import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  BookOpen,
  AlertTriangle,
  Settings,
  Menu,
  X,
  Bot,
  FileText,
  ChevronLeft,
  Users,
  GitBranch,
  Smartphone,
  Database,
  Zap,
  LogOut,
  Megaphone,
  BarChart3,
  Sparkles,
  LifeBuoy,
  Terminal,
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/AuthContext';

// ─────────────────────────────────────────────────────────────
// Two distinct product sections — Support AI and Outreach AI.
// Each agent has its own isolated workflows, settings and analytics.
// ─────────────────────────────────────────────────────────────

const SUPPORT_NAV = [
  { path: '/support/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/support/conversations', icon: MessageSquare, label: 'Conversations' },
  { path: '/support/knowledge-hub', icon: Database, label: 'Knowledge Hub' },
  { path: '/support/knowledge', icon: BookOpen, label: 'Knowledge Base' },
  { path: '/support/troubleshooting', icon: AlertTriangle, label: 'Troubleshooting' },
  { path: '/support/whatsapp', icon: Smartphone, label: 'WhatsApp Web' },
  { path: '/support/logs', icon: FileText, label: 'Webhook Logs' },
  { path: '/support/settings', icon: Settings, label: 'Support AI Settings' },
];

const OUTREACH_NAV = [
  { path: '/outreach/dashboard', icon: LayoutDashboard, label: 'Outreach Dashboard' },
  { path: '/outreach/leads', icon: Users, label: 'Leads / CRM' },
  { path: '/outreach/pipeline', icon: GitBranch, label: 'Pipeline' },
  { path: '/outreach/campaigns', icon: Megaphone, label: 'Campaigns' },
  { path: '/outreach/logs', icon: Terminal, label: 'Activity Logs' },
  { path: '/outreach/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/outreach/settings', icon: Sparkles, label: 'Outreach AI Settings' },
];

const NavSection = ({ title, color, icon: SectionIcon, items, sidebarOpen }) => (
  <div className="mb-4" data-testid={`nav-section-${title.toLowerCase()}`}>
    {sidebarOpen ? (
      <div className="flex items-center gap-2 px-3 py-2 mb-1">
        <SectionIcon className={cn('w-3.5 h-3.5', color)} />
        <span className={cn(
          'text-[10px] font-semibold uppercase tracking-[0.18em]',
          color,
        )}>
          {title}
        </span>
      </div>
    ) : (
      <div className="h-px bg-border/40 my-2 mx-3" />
    )}
    <nav className="space-y-0.5 px-2">
      {items.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          className={({ isActive }) => cn(
            'flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200',
            'hover:bg-muted/50 group',
            isActive
              ? 'bg-primary/10 border-l-2 border-l-primary text-primary'
              : 'text-muted-foreground hover:text-foreground',
          )}
          data-testid={`nav-${item.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
        >
          <item.icon className={cn(
            'w-4 h-4 flex-shrink-0',
            sidebarOpen ? '' : 'mx-auto',
          )} />
          {sidebarOpen && (
            <span className="text-sm font-medium">{item.label}</span>
          )}
        </NavLink>
      ))}
    </nav>
  </div>
);

export const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Detect which product the current route belongs to (for a small header badge)
  const activeProduct = location.pathname.startsWith('/outreach')
    ? { name: 'Outreach AI', color: 'text-amber-400', icon: Zap }
    : { name: 'Support AI', color: 'text-emerald-400', icon: LifeBuoy };

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="dashboard-layout">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:relative inset-y-0 left-0 z-50 flex flex-col',
          'border-r border-border bg-card/50 backdrop-blur-xl transition-all duration-300',
          sidebarOpen ? 'w-64' : 'w-16',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-border">
          {sidebarOpen && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Bot className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-semibold text-lg tracking-tight">Monk Algo</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            <ChevronLeft className={cn('w-4 h-4 transition-transform', !sidebarOpen && 'rotate-180')} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation — two distinct product sections */}
        <ScrollArea className="flex-1 py-4">
          <NavSection
            title="Support AI"
            color="text-emerald-400"
            icon={LifeBuoy}
            items={SUPPORT_NAV}
            sidebarOpen={sidebarOpen}
          />
          <div className="border-t border-border/40 mx-3 my-2" />
          <NavSection
            title="Outreach AI"
            color="text-amber-400"
            icon={Zap}
            items={OUTREACH_NAV}
            sidebarOpen={sidebarOpen}
          />
        </ScrollArea>

        {/* Footer */}
        {sidebarOpen && (
          <div className="p-4 border-t border-border space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>Bot Active</span>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground hover:text-red-400" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />Sign Out
            </Button>
          </div>
        )}
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 border-b border-border bg-card/30 backdrop-blur-sm flex items-center justify-between px-4 lg:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu"
          >
            <Menu className="w-5 h-5" />
          </Button>

          {/* Active product badge — visually reinforces the agent split */}
          <div
            className={cn(
              'hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full',
              'border border-border bg-card/40 backdrop-blur',
            )}
            data-testid="active-product-badge"
          >
            <activeProduct.icon className={cn('w-3.5 h-3.5', activeProduct.color)} />
            <span className={cn('text-xs font-medium', activeProduct.color)}>
              {activeProduct.name}
            </span>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            <span className="text-sm text-muted-foreground hidden sm:block">
              {user?.email || 'WhatsApp Support Agent'}
            </span>
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <Bot className="w-4 h-4 text-primary" />
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
